import { useEffect, useState } from "react";
import {
  generateCancelCode,
  cancelReservation,
} from "../api/services/reservationService";
import { TriangleAlert, Trash2, ArrowLeft, CircleCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const OTP_VALIDITY_MS = 15 * 60 * 1000;

export function CancelBookingPage({ booking, email, onBack, onCancelled }) {
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpTimer, setOtpTimer] = useState(0);

  const [selectedReason, setSelectedReason] = useState("");
  const [comments, setComments] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [globalAlert, setGlobalAlert] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const canResendOtp = otpSent && otpTimer === 0 && !otpLoading;
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [policyError, setPolicyError] = useState("");
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyInfo, setPolicyInfo] = useState([]);
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const navigate = useNavigate();

  const CANCELLATION_REASONS = [
    "The rental is too expensive",
    "I booked another vehicle elsewhere",
    "My trip was cancelled",
    "The location of my trip changed",
    "The timing of my trip changed",
    "Other reason",
  ];

  useEffect(() => {
    if (globalAlert) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [globalAlert]);

  const handleConfirmCancel = async () => {
    // ... validation logic ...
    setLoading(true);

    try {
      const res = await cancelReservation({
        emailId: email,
        reservationNumber: booking.reservation_no,
        otpCode: otp,
        reason:
          selectedReason === "Other reason"
            ? `${selectedReason} - ${comments}`
            : selectedReason,
      });

      if (res?.status === "SUCCESS") {
        setShowConfirm(false);
        setSuccess(true);
      } else {
        setError(res?.message || "Cancellation failed.");
      }
    } catch (err) {
      setGlobalAlert(err?.response?.data?.message || "Something went wrong.");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setError("");
    setInfoMessage("");
    setOtpError("");
    setOtpLoading(true);

    try {
      // 1. Hit the API
      const response = await generateCancelCode({
        emailId: email,
        reservationNumber: booking.reservation_no,
      });

      // 2. SUCCESS (200): Use the message directly from the response body
      const successMsg =
        response?.data?.message || "A verification code has been sent.";

      setOtpSent(true);
      setOtpExpiresAt(Date.now() + OTP_VALIDITY_MS);
      setOtpTimer(Math.floor(OTP_VALIDITY_MS / 1000));
      setInfoMessage(successMsg);
    } catch (err) {
      const status = err?.response?.status;
      const backendMsg = err?.response?.data?.message || "Failed to send code.";

      if (status === 409) {
        // 3. CONFLICT (409): Also a successful flow for the user
        // We use the message from the API (e.g., "OTP is already sent...")
        setOtpSent(true);
        setInfoMessage(backendMsg);
        setError(""); // Ensure no red error text shows up
      } else {
        // 4. ACTUAL ERROR (400, 500, etc.)
        setError(backendMsg);
      }
    } finally {
      setOtpLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!otpExpiresAt) return;

    const interval = setInterval(() => {
      const remaining = otpExpiresAt - Date.now();
      setOtpTimer(Math.max(0, Math.floor(remaining / 1000)));
    }, 1000);

    return () => clearInterval(interval);
  }, [otpExpiresAt]);

  return (
    <div className="min-h-screen bg-gray-50 mt-20">
      <div
        className="max-w-xl mx-auto px-6 py-8 space-y-4"
        style={{ maxWidth: "620px" }}
      >
        {/* Global Alert */}
        {globalAlert && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-4 rounded-lg font-body">
            {globalAlert}
          </div>
        )}

        {/* Back link */}
        {!success && (
          <button
            onClick={success ? onCancelled : onBack}
            className="font-semibold flex items-center gap-2 text-sm text-gray-600 hover:text-black font-heading transition-colors"
          >
            <ArrowLeft size={15} /> Back to Manage Booking
          </button>
        )}

        {/* Card — switches based on success */}
        {success ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-10 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mb-5">
              <CircleCheck size={28} className="text-primary" />
            </div>
            <h2 className="font-heading font-black text-2xl tracking-normal text-gray-900 mb-3">
              Cancellation confirmed
            </h2>
            <p className="font-heading text-sm text-gray-500 max-w-lg leading-relaxed mb-4">
              We've received your request. A confirmation email will arrive
              within a few minutes.
            </p>
            <button
              onClick={() => navigate("/")}
              className="bg-primary hover:bg-primary-hover text-sm text-white font-heading px-4 py-2 rounded-lg transition-colors"
            >
              Back to Manage Booking
            </button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {/* Warning Card */}
            <div className="bg-red-50/60 p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <TriangleAlert size={20} className="text-red-500" />
              </div>
              <div>
                <h2 className="font-heading font-black text-gray-900 text-lg">
                  Cancel Booking
                </h2>
                <p className="font-heading text-sm text-gray-600 mt-1">
                  This action can't be undone. Refund (if any) follows the
                  cancellation policy.
                </p>
              </div>
            </div>

            {/* OTP Section */}
            <div className="p-4">
              <p className="font-heading text-xs font-bold text-primary uppercase tracking-widest mb-1">
                OTP Verification
              </p>
              <p className="font-body text-sm text-gray-500 mb-4">
                {infoMessage ||
                  "We sent a 6-digit code to your registered email."}
              </p>

              {!otpSent ? (
                <button
                  onClick={handleSendOtp}
                  disabled={otpLoading}
                  className="w-full bg-primary hover:bg-primary-hover text-white py-3 rounded-xl font-semibold text-sm transition disabled:opacity-60"
                >
                  {otpLoading ? "Sending..." : "Send Verification Code"}
                </button>
              ) : (
                <div className="grid grid-cols-6 gap-2 mb-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <input
                      key={i}
                      id={`otp-${i}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={otp[i] || ""}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        const otpArr = otp.split("");
                        otpArr[i] = val;
                        const newOtp = otpArr.join("").slice(0, 6);
                        setOtp(newOtp);
                        setOtpError("");
                        if (val && i < 5) {
                          document.getElementById(`otp-${i + 1}`)?.focus();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !otp[i] && i > 0) {
                          document.getElementById(`otp-${i - 1}`)?.focus();
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = e.clipboardData
                          .getData("text")
                          .replace(/\D/g, "")
                          .slice(0, 6);
                        setOtp(pasted);
                        setOtpError("");
                        document
                          .getElementById(`otp-${Math.min(pasted.length, 5)}`)
                          ?.focus();
                      }}
                      className={`w-full h-12 text-center text-lg font-heading font-bold border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-black shadow-sm transition ${
                        otpError ? "border-red-400" : "border-gray-200"
                      }`}
                    />
                  ))}
                </div>
              )}

              {otpError && (
                <p className="text-sm text-red-600 font-body mb-2">
                  {otpError}
                </p>
              )}

              <button
                type="button"
                disabled={!canResendOtp}
                onClick={handleSendOtp}
                className={`text-xs font-semibold font-heading ${
                  canResendOtp
                    ? "text-primary hover:underline"
                    : "text-gray-400 cursor-not-allowed"
                }`}
              >
                Resend OTP
              </button>
            </div>

            <hr className="border-gray-100" />

            {/* Reason Section */}
            <div className="p-4">
              <p className="font-heading text-xs font-bold text-primary uppercase tracking-widest mb-3">
                Reason for Cancellation
              </p>
              <select
                value={selectedReason}
                disabled={!otpSent}
                onChange={(e) => {
                  setSelectedReason(e.target.value);
                  setReasonError("");
                }}
                className={`w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-primary transition ${
                  !otpSent
                    ? "bg-gray-50 cursor-not-allowed text-gray-400"
                    : "bg-white"
                }`}
              >
                <option value="">Select a reason...</option>
                {CANCELLATION_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              {reasonError && (
                <p className="text-sm text-red-600 mt-2 font-body">
                  {reasonError}
                </p>
              )}
            </div>

            <hr className="border-gray-100" />

            {/* Additional Notes Section */}
            <div className="p-4">
              <p className="font-heading text-xs font-bold uppercase tracking-widest mb-3">
                <span className="text-primary">Additional Notes</span>{" "}
                <span className="text-gray-400">(Optional)</span>
              </p>
              <textarea
                rows={4}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Anything we should know?"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-primary transition resize-none"
              />
            </div>

            <hr className="border-gray-100" />

            {/* Bottom action bar */}
            <div className="px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={onBack}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-heading text-black hover:bg-primary/20 transition"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (!policyAccepted) {
                    setPolicyAccepted(true);
                  }
                  setShowConfirm(true);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                disabled={
                  loading ||
                  !otpSent ||
                  otp.length !== 6 ||
                  !selectedReason ||
                  (selectedReason === "Other reason" &&
                    comments.trim().length < 15)
                }
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-body text-white transition-all ${
                  loading ||
                  !otpSent ||
                  otp.length !== 6 ||
                  !selectedReason ||
                  (selectedReason === "Other reason" &&
                    comments.trim().length < 15)
                    ? "bg-red-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700 active:scale-95"
                }`}
              >
                <Trash2 size={15} />
                {loading ? "Cancelling..." : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Modal — untouched */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="font-heading text-lg font-black text-gray-900">
              Confirm Cancellation
            </h3>
            <p className="font-body text-sm text-gray-600 mt-3">
              Are you sure you want to cancel this booking?
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 font-body text-sm"
              >
                No
              </button>
              <button
                onClick={() => {
                  setShowConfirm(false);
                  handleConfirmCancel();
                }}
                className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 font-body text-sm"
              >
                Yes, Cancel Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Policy Modal — untouched */}
      {showPolicyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
          onClick={() => setShowPolicyModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-lg max-w-lg w-full mx-4 p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowPolicyModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-xl"
            >
              &times;
            </button>
            <h2 className="font-heading text-lg font-black text-primary mb-4">
              Cancellation Policy & Terms
            </h2>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto font-body">
              {loadingPolicy && (
                <p className="text-sm text-gray-600">Loading policy…</p>
              )}
              {!loadingPolicy &&
                policyInfo.length > 0 &&
                policyInfo.map((policy, idx) => (
                  <div key={idx}>
                    <p className="font-semibold text-gray-800">
                      {policy.policyType}
                    </p>
                    <p className="text-sm text-gray-700 whitespace-pre-line">
                      {policy.policyText?.trim() ||
                        "Details will be provided at the counter."}
                    </p>
                  </div>
                ))}
              {!loadingPolicy && policyInfo.length === 0 && (
                <p className="text-sm text-gray-600">
                  No policy information available.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
